import {
  EKycVerificationStatus,
  ExamEventExamineeStatus,
  FilterOperator,
  useSearchExamineeEKycStatusesInExamEventQuery,
} from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import { Box, DataGrid } from '@shared/components';
import { useMemo } from 'react';

import { useProctoringEKycListSearchParams } from '../../hooks';

import { columns } from './columns';
import ProctoringEKycListNoRowsOverlay from './proctoring-ekyc-list-no-row-over-lay';

export default function ProctoringEKycList() {
  const navigate = useNavigate();
  const [{ page, pageSize, search }, { changePageNumber, changePageSize }] =
    useProctoringEKycListSearchParams();
  const { examCenterCode, examEventId } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc',
  );

  const { data: searchExamineeEKycStatusesResult, loading: searchingForExamEventExaminees } =
    useSearchExamineeEKycStatusesInExamEventQuery({
      variables: {
        searchExamineeEKycStatusesInExamEvent: {
          searchExaminee: {
            filter: {
              searchText: {
                fields: ['fullName'],
                value: search,
              },
            },
          },
          filter: {
            conditions: [
              {
                field: 'status',
                operator: FilterOperator.Equal,
                value: ExamEventExamineeStatus.PrepareForExam,
              },
            ],
          },
          eKycStatuses: [EKycVerificationStatus.PendingApproval, EKycVerificationStatus.Rejected],
          paging: {
            currentPage: page,
            pageSize: pageSize,
          },
          examEventId,
        },
      },
    });

  const { examineeEKycStatuses = [], total = 0 } =
    searchExamineeEKycStatusesResult?.ecSearchExamineeEKycStatusesInExamEvent ?? {};
  const rows = useMemo(() => {
    // `sourceEKycStatus` is the row-level value resolved from kyc-api — deliberately not
    // `examinee.eKycStatus`, which is exam-center's own copy and can be stale.
    return examineeEKycStatuses.map(({ sourceEKycStatus, examEventExaminee }) => {
      const { email, examinee } = examEventExaminee;

      return {
        id: examinee.id,
        name: [examinee.title, examinee.firstName, examinee.middleName, examinee.lastName]
          .filter(Boolean)
          .join(' '),
        email,
        status: sourceEKycStatus,
      };
    });
  }, [examineeEKycStatuses]);

  return (
    <Box>
      <Box sx={(theme) => ({ px: theme.spacing(6), pb: theme.spacing(6) })}>
        <DataGrid
          loading={searchingForExamEventExaminees}
          columns={columns}
          rows={rows}
          onRowClick={(row) => {
            navigate('/:examCenterCode/proctoring/exam-events/:examEventId/e-kyc/:examineeId', {
              params: {
                examCenterCode: examCenterCode,
                examEventId: examEventId,
                examineeId: String(row.id),
              },
            });
          }}
          rowHeight={48}
          disableColumnMenu
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.background.paper,
            minHeight: '100%',
            '.MuiDataGrid-cell': {
              p: theme.spacing(3),
            },
            '.MuiDataGrid-columnHeader': {
              backgroundColor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              p: theme.spacing(3),
            },
            '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            },
            '--DataGrid-overlayHeight': '352px',
            '.MuiBox-root > .MuiTypography-root': {
              color: theme.palette.text.secondary,
            },
          })}
          slots={{
            noRowsOverlay: ProctoringEKycListNoRowsOverlay,
          }}
          slotProps={{
            footer: {
              total: total,
              pagination: {
                page: page,
                count: total,
                rowsPerPage: pageSize,
                onPageChange: (_event, page) => {
                  changePageNumber(page);
                },
                onRowsPerPageChange: (event) => {
                  const pageSize = parseInt(event.target.value);
                  changePageNumber(0);
                  return changePageSize(pageSize);
                },
              },
            },
          }}
        />
      </Box>
    </Box>
  );
}
